import React, { useState, useEffect } from 'react'
import { StepProps } from '../../../../components/_WMS/Stepper'
import { SharedStateType } from '../../types'
import { Box, TextField, Typography, InputAdornment } from '@mui/material'
import { StepHeader } from '../../components'
import { stepperStyles } from '../styles'
import { LoadingButton } from '../../../../components/_EOS/Button/LoadingButton'
import { ScanIcon } from '../../../../components/_WMS/_icons/ScanIcon'

const ICON_SCALE = 5
const ICON_W = 22 * ICON_SCALE // 110px
const ICON_H = 16 * ICON_SCALE // 80px
const CORNER_SIZE = 22
const CORNER_COLOR = '#1d4ed8'
const CORNER_THICKNESS = 3

const corners: React.CSSProperties[] = [
  {
    top: 0,
    left: 0,
    borderTop: `${CORNER_THICKNESS}px solid ${CORNER_COLOR}`,
    borderLeft: `${CORNER_THICKNESS}px solid ${CORNER_COLOR}`
  },
  {
    top: 0,
    right: 0,
    borderTop: `${CORNER_THICKNESS}px solid ${CORNER_COLOR}`,
    borderRight: `${CORNER_THICKNESS}px solid ${CORNER_COLOR}`
  },
  {
    bottom: 0,
    left: 0,
    borderBottom: `${CORNER_THICKNESS}px solid ${CORNER_COLOR}`,
    borderLeft: `${CORNER_THICKNESS}px solid ${CORNER_COLOR}`
  },
  {
    bottom: 0,
    right: 0,
    borderBottom: `${CORNER_THICKNESS}px solid ${CORNER_COLOR}`,
    borderRight: `${CORNER_THICKNESS}px solid ${CORNER_COLOR}`
  }
]

const BarcodeDisplay: React.FC = () => (
  <Box
    style={{
      position: 'relative',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: ICON_W + CORNER_SIZE * 7,
      height: ICON_H + CORNER_SIZE * 1,
      margin: '0 auto'
    }}
  >
    {corners.map((style, i) => (
      <Box
        key={i}
        style={{
          position: 'absolute',
          width: CORNER_SIZE,
          height: CORNER_SIZE,
          ...style
        }}
      />
    ))}
    <Box
      sx={{
        transform: `scale(${ICON_SCALE})`,
        transformOrigin: 'center',
        display: 'flex',
        lineHeight: 0,
        '& svg': {
          color: '#000'
        }
      }}
    >
      <ScanIcon />
      <ScanIcon />
    </Box>
  </Box>
)

export const WaitingScreen: React.FC<StepProps<SharedStateType>> = ({
  next,
  sharedState: { workOrderId, positionCode, zoneName, warehouseName },
  setSharedState
}) => {
  const [confirmedPosition, setConfirmedPosition] = useState('')
  const [error, setError] = useState('')

   useEffect(() => {
      setSharedState((prev) => ({ ...prev, stepTitle: 'Posición asignada' }))
      return () => {
        setSharedState((prev) => ({ ...prev, stepTitle: undefined }))
      }
    }, [])

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (confirmedPosition !== positionCode) {
      setError(`Posición escaneada no corresponde a posición ${positionCode}`)
      return
    }
    setError('')
    next()
  }

  return (
    <Box sx={stepperStyles.root}>
      <Box
        sx={stepperStyles.content}
        style={{
          justifyContent: 'flex-start',
          paddingTop: 10,
          overflowY: 'auto'
        }}
      >
        <Box style={{ width: '100%', padding: '0 16px' }}>        
          {/* Info chips */}
          <StepHeader
            fields={[
              { label: 'Conteo', value: workOrderId ?? '—' },
              { label: 'Almacén', value: warehouseName },
              { label: 'Zona', value: zoneName }
            ]}
          />

          {/* Destino */}
          <Box style={{ textAlign: 'center', marginBottom: 15 }}>
            <Typography
              variant='body2'
              style={{ color: '#666', marginBottom: 5 }}
            >
              Dirígete a:
            </Typography>
            <Typography
              variant='h4'
              style={{ fontWeight: 800, marginBottom: 5 }}
            >
              {positionCode}
            </Typography>
            <BarcodeDisplay />
          </Box>

          {/* Scan + Aceptar */}
          <form onSubmit={handleSubmit}>
            <TextField
              fullWidth
              autoFocus
              variant='outlined'
              placeholder='Escanea o escribe la posición'
              value={confirmedPosition}
              onChange={(e) => {
                setConfirmedPosition(e.target.value)
                if (error) setError('')
              }}
              error={error.length > 0}
              helperText={error}
              size='small'
              style={{ marginBottom: 12 }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position='end'>
                    <ScanIcon />
                  </InputAdornment>
                )
              }}
            />
            <LoadingButton
              loading={false}
              variant='contained'
              color='primary'
              fullWidth
              type='submit'
              style={{
                borderRadius: 24,
                textTransform: 'none',
                fontWeight: 700
              }}
            >
              Aceptar
            </LoadingButton>
          </form>
        </Box>
      </Box>
    </Box>
  )
}
